export const EXCHANGE_MAP_TYPES = {
  EXPORT: 'export',
  IMPORT: 'import',
  GENERAL: 'general',
  POST_IMPORT: 'postImport',
  FREE_CARRIERS: 'freeCarriers'
}

export const isRoundTripSuggestionEnabled = exchangeType =>
  exchangeType === EXCHANGE_MAP_TYPES.IMPORT

export const getRoundTripCandidateTypes = exchangeType =>
  exchangeType === EXCHANGE_MAP_TYPES.IMPORT
    ? [EXCHANGE_MAP_TYPES.EXPORT, EXCHANGE_MAP_TYPES.GENERAL]
    : []

const ROUND_TRIP_SHIPOWNER_NAME = 'IMPORT+EXPORT(KOLKO)'

const exchangeMapConfig = {
  [EXCHANGE_MAP_TYPES.EXPORT]: {
    fromLocation: 'loadingLocation',
    toLocation: 'returnLocation',
    fromDate: 'loadingDate',
    fromTime: 'loadingTime',
    toDate: 'returnDate',
    toTime: 'returnTime',
    container: 'container'
  },
  [EXCHANGE_MAP_TYPES.IMPORT]: {
    fromLocation: 'pickupLocation',
    toLocation: 'unloadingLocation',
    fromDate: 'pickupDate',
    fromTime: 'pickupTime',
    toDate: 'unloadingDate',
    toTime: 'unloadingTime',
    container: 'container'
  },
  [EXCHANGE_MAP_TYPES.GENERAL]: {
    fromLocation: 'loadingLocation',
    toLocation: 'unloadingLocation',
    fromDate: 'loadingDate',
    fromTime: 'loadingTime',
    toDate: 'unloadingDate',
    toTime: 'unloadingTime',
    container: 'container'
  },
  [EXCHANGE_MAP_TYPES.POST_IMPORT]: {
    fromLocation: 'availabilityLocation',
    toLocation: 'destinationLocation',
    fromDate: 'availabilityDate',
    fromTime: 'availabilityTime',
    container: 'container'
  },
  [EXCHANGE_MAP_TYPES.FREE_CARRIERS]: {
    fromLocation: 'availabilityLocation',
    toLocation: 'destinationLocation',
    fromDate: 'availabilityDate',
    fromTime: 'availabilityTime',
    container: 'containerSemiTrailer'
  }
}

const getCoordinate = (location, names) => {
  if (!location) return null

  const name = names.find(key => {
    const value = location[key]
    return value !== null && value !== undefined && value !== ''
  })
  if (!name) return null

  const coordinate = Number(location[name])
  return Number.isFinite(coordinate) ? coordinate : null
}

export const normalizeMapLocation = location => {
  let geoJsonCoordinates = []
  if (
    location &&
    location.geometry &&
    Array.isArray(location.geometry.coordinates)
  ) {
    geoJsonCoordinates = location.geometry.coordinates
  } else if (location && Array.isArray(location.coordinates)) {
    geoJsonCoordinates = location.coordinates
  }
  const directLat = getCoordinate(location, ['lat', 'latitude'])
  const directLong = getCoordinate(location, [
    'long',
    'lng',
    'lon',
    'longitude'
  ])
  const lat =
    directLat === null && geoJsonCoordinates.length >= 2
      ? Number(geoJsonCoordinates[1])
      : directLat
  const long =
    directLong === null && geoJsonCoordinates.length >= 2
      ? Number(geoJsonCoordinates[0])
      : directLong

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(long) ||
    Math.abs(lat) > 90 ||
    Math.abs(long) > 180
  ) {
    return null
  }

  return {
    id: location.id,
    name: location.name || '',
    countryCode: location.countryCode || '',
    coordinates: [long, lat]
  }
}

const getContainerName = (offer, config) => {
  const container = offer[config.container]
  return container && container.name ? container.name : ''
}

const getCompanyName = offer => {
  const issuer = offer.issuer
  return issuer && issuer.company && issuer.company.name
    ? issuer.company.name
    : ''
}

const getShipownerName = offer =>
  offer.shipowner && offer.shipowner.name ? offer.shipowner.name : ''

const getShipownerId = offer =>
  offer.shipownerId ||
  (offer.shipowner && offer.shipowner.id) ||
  (offer.raw && offer.raw.shipownerId) ||
  (offer.raw && offer.raw.shipowner && offer.raw.shipowner.id) ||
  null

const getContainerId = offer =>
  offer.containerId ||
  (offer.container && offer.container.id) ||
  (offer.raw && offer.raw.containerId) ||
  (offer.raw && offer.raw.container && offer.raw.container.id) ||
  null

const getDateTime = (offer, dateKey, timeKey) =>
  [offer[dateKey], offer[timeKey]].filter(Boolean).join(', ')

const getRawOfferValue = (offer, key) =>
  offer.raw && offer.raw[key] ? offer.raw[key] : offer[key]

export const normalizeMapOffer = (offer, exchangeType) => {
  const config = exchangeMapConfig[exchangeType]
  if (!config) return null

  const fromLocation = offer[config.fromLocation]
  const toLocation = offer[config.toLocation]

  return {
    id: offer.id,
    exchangeType,
    referenceNumber: offer.referenceNumber,
    offer,
    containerId: getContainerId(offer),
    containerName: getContainerName(offer, config),
    companyName: getCompanyName(offer),
    shipownerId: getShipownerId(offer),
    shipownerName: getShipownerName(offer),
    from: {
      location: fromLocation,
      mapLocation: normalizeMapLocation(fromLocation),
      date: offer[config.fromDate] || '',
      rawDate: getRawOfferValue(offer, config.fromDate) || '',
      dateTime: getDateTime(offer, config.fromDate, config.fromTime)
    },
    to: {
      location: toLocation,
      mapLocation: normalizeMapLocation(toLocation),
      date: config.toDate ? offer[config.toDate] || '' : '',
      rawDate: config.toDate
        ? getRawOfferValue(offer, config.toDate) || ''
        : '',
      dateTime: config.toDate
        ? getDateTime(offer, config.toDate, config.toTime)
        : ''
    }
  }
}

const getMapPointName = point =>
  point.type === 'city' && point.postalCode
    ? `${point.name} (${point.postalCode})`
    : point.name

export const normalizeMapLayers = layers =>
  (layers || []).reduce((groups, layer) => {
    const layerGroups = (layer.points || [])
      .map(point => {
        const mapLocation = normalizeMapLocation({
          ...point,
          id: point.locationId,
          name: getMapPointName(point)
        })
        if (!mapLocation || !point.locationId) return null

        return {
          id: `${layer.key}:${point.locationId}`,
          role: layer.key,
          layerRole: layer.role,
          mapLocation: {
            ...mapLocation,
            postalCode: point.postalCode || '',
            type: point.type || ''
          },
          offers: [],
          offersCount: Number(point.offersCount) || 0,
          locationIds: [point.locationId],
          points: [point]
        }
      })
      .filter(Boolean)

    return groups.concat(layerGroups)
  }, [])

export const hydrateMapOfferLocations = (mapOffer, locationsById) => {
  if (!mapOffer || !locationsById) return mapOffer

  const hydrateEndpoint = endpoint => {
    if (!endpoint || endpoint.mapLocation || !endpoint.location) return endpoint

    const mapLocation = locationsById.get(String(endpoint.location.id))
    return mapLocation ? { ...endpoint, mapLocation } : endpoint
  }

  return {
    ...mapOffer,
    from: hydrateEndpoint(mapOffer.from),
    to: hydrateEndpoint(mapOffer.to)
  }
}

export const normalizeRoundTripMapOffers = roundTripOffers => {
  const candidateLocationsByExchange = new Map()

  return (roundTripOffers || [])
    .map(({ exchangeType, offer, mapLayers }) => {
      if (!candidateLocationsByExchange.has(exchangeType)) {
        const candidateLocations = new Map()
        normalizeMapLayers(mapLayers).forEach(group => {
          group.locationIds.forEach(locationId => {
            candidateLocations.set(String(locationId), group.mapLocation)
          })
        })
        candidateLocationsByExchange.set(exchangeType, candidateLocations)
      }

      return hydrateMapOfferLocations(
        normalizeMapOffer(offer, exchangeType),
        candidateLocationsByExchange.get(exchangeType)
      )
    })
    .filter(Boolean)
}

export const ROUND_TRIP_LIVE_HIGHLIGHT_MS = 2 * 60 * 1000

export const getRoundTripOfferKey = offer =>
  `${offer.exchangeType}:${
    offer.id === undefined || offer.id === null ? offer.offer.id : offer.id
  }`

export const reconcileRoundTripLiveOffers = (
  previousSnapshot,
  offers,
  previousTimes = {},
  now = Date.now(),
  successfulExchangeTypes = Array.from(
    new Set(offers.map(offer => offer.exchangeType))
  )
) => {
  const refreshedTypes = new Set(successfulExchangeTypes)
  const previousOfferKeys = previousSnapshot && previousSnapshot.offerKeys
  const loadedExchangeTypes = new Set(
    previousSnapshot ? previousSnapshot.loadedExchangeTypes : []
  )
  // A failed exchange is not an empty exchange. Keep its last known keys and
  // do not mark its first successful response as a batch of new offers.
  const offerKeys = new Set(
    previousOfferKeys
      ? Array.from(previousOfferKeys).filter(
          key => !refreshedTypes.has(key.split(':')[0])
        )
      : []
  )
  const liveOfferTimes = {}

  offers.forEach(offer => {
    const key = getRoundTripOfferKey(offer)
    offerKeys.add(key)
    if (
      loadedExchangeTypes.has(offer.exchangeType) &&
      previousOfferKeys &&
      !previousOfferKeys.has(key)
    ) {
      liveOfferTimes[key] = now
    }
  })
  offerKeys.forEach(key => {
    const markedAt = previousTimes[key]
    if (
      markedAt !== undefined &&
      now - markedAt < ROUND_TRIP_LIVE_HIGHLIGHT_MS
    ) {
      liveOfferTimes[key] = markedAt
    }
  })
  successfulExchangeTypes.forEach(type => loadedExchangeTypes.add(type))

  return { offerKeys, loadedExchangeTypes, liveOfferTimes }
}

const normalizeShipownerName = value =>
  String(value || '')
    .toLocaleUpperCase('pl-PL')
    .replace(/Ó/g, 'O')
    .replace(/Ł/g, 'L')
    .replace(/\s/g, '')

const matchesRoundTripDictionaryFilter = ({
  candidateId,
  candidateName,
  filterIds,
  sourceId,
  sourceName
}) => {
  if (filterIds.size === 0) return true
  if (
    candidateId !== null &&
    candidateId !== undefined &&
    filterIds.has(String(candidateId))
  ) {
    return true
  }

  const usesSourceDefault =
    sourceId !== null &&
    sourceId !== undefined &&
    filterIds.size === 1 &&
    filterIds.has(String(sourceId))

  return Boolean(
    usesSourceDefault &&
      normalizeShipownerName(candidateName) &&
      normalizeShipownerName(candidateName) ===
        normalizeShipownerName(sourceName)
  )
}

export const isPreplannedRoundTripOffer = mapOffer =>
  normalizeShipownerName(mapOffer && mapOffer.shipownerName) ===
  ROUND_TRIP_SHIPOWNER_NAME

const getLocationNameIdentity = location =>
  [location.countryCode, location.name]
    .map(value =>
      String(value || '')
        .replace(/\s*\([^)]*\)\s*$/, '')
        .trim()
        .toLocaleLowerCase('pl-PL')
    )
    .join(':')

export const getOfferLocationMatchKeys = location => {
  if (!location) return []

  const keys = []
  if (location.id !== null && location.id !== undefined) {
    keys.push(`id:${String(location.id)}`)
  }
  const nameIdentity = getLocationNameIdentity(location)
  if (nameIdentity !== ':') keys.push(`name:${nameIdentity}`)

  return keys
}

export const isSameOfferLocation = (firstLocation, secondLocation) => {
  if (!firstLocation || !secondLocation) return false

  const hasMatchingIds =
    firstLocation.id !== null &&
    firstLocation.id !== undefined &&
    secondLocation.id !== null &&
    secondLocation.id !== undefined &&
    String(firstLocation.id) === String(secondLocation.id)

  const firstNameIdentity = getLocationNameIdentity(firstLocation)
  const secondNameIdentity = getLocationNameIdentity(secondLocation)
  const hasMatchingNames =
    firstNameIdentity !== ':' && firstNameIdentity === secondNameIdentity

  return hasMatchingIds || hasMatchingNames
}

export const getRoundTripCenterMapLocation = (
  selectedMapOffer,
  candidateMapLocations = []
) => {
  if (!selectedMapOffer || !selectedMapOffer.to) return null
  if (selectedMapOffer.to.mapLocation) return selectedMapOffer.to.mapLocation

  return (
    candidateMapLocations.find(mapLocation =>
      isSameOfferLocation(selectedMapOffer.to.location, mapLocation)
    ) || null
  )
}

const getDateDayNumber = value => {
  const dateMatch = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!dateMatch) return null
  return Date.UTC(
    Number(dateMatch[1]),
    Number(dateMatch[2]) - 1,
    Number(dateMatch[3])
  )
}

const isSameDay = (firstDate, secondDate) => {
  const firstDay = getDateDayNumber(firstDate)
  const secondDay = getDateDayNumber(secondDate)

  return firstDay === null || secondDay === null
    ? Boolean(
        firstDate && secondDate && String(firstDate) === String(secondDate)
      )
    : firstDay === secondDay
}

const isDateWithinDays = (startDate, candidateDate, daysAhead) => {
  if (Number(daysAhead) === 0) return isSameDay(startDate, candidateDate)

  const startDay = getDateDayNumber(startDate)
  const candidateDay = getDateDayNumber(candidateDate)
  if (startDay === null || candidateDay === null) return false

  const differenceInDays = (candidateDay - startDay) / (24 * 60 * 60 * 1000)
  return differenceInDays >= 0 && differenceInDays <= Number(daysAhead)
}

export const getMapDistanceKm = (firstLocation, secondLocation) => {
  if (
    !firstLocation ||
    !secondLocation ||
    !Array.isArray(firstLocation.coordinates) ||
    !Array.isArray(secondLocation.coordinates)
  ) {
    return null
  }

  const toRadians = value => (value * Math.PI) / 180
  const [firstLongitude, firstLatitude] = firstLocation.coordinates
  const [secondLongitude, secondLatitude] = secondLocation.coordinates
  const coordinates = [
    firstLongitude,
    firstLatitude,
    secondLongitude,
    secondLatitude
  ].map(Number)
  if (!coordinates.every(Number.isFinite)) return null

  const [normalizedFirstLongitude, normalizedFirstLatitude] = coordinates
  const [
    normalizedSecondLongitude,
    normalizedSecondLatitude
  ] = coordinates.slice(2)
  const latitudeDistance = toRadians(
    normalizedSecondLatitude - normalizedFirstLatitude
  )
  const longitudeDistance = toRadians(
    normalizedSecondLongitude - normalizedFirstLongitude
  )
  const firstLatitudeRadians = toRadians(normalizedFirstLatitude)
  const secondLatitudeRadians = toRadians(normalizedSecondLatitude)
  const haversine =
    Math.sin(latitudeDistance / 2) ** 2 +
    Math.cos(firstLatitudeRadians) *
      Math.cos(secondLatitudeRadians) *
      Math.sin(longitudeDistance / 2) ** 2

  return 6371 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
}

const getRoundTripListingTime = mapOffer => {
  const offer = mapOffer.offer || {}
  const rawOffer = offer.raw || offer
  const listedAt =
    rawOffer.addedToListingAt ||
    offer.addedToListingAt ||
    rawOffer.createdAt ||
    offer.createdAt
  const parsedTime = Date.parse(listedAt)
  return Number.isFinite(parsedTime) ? parsedTime : 0
}

export const findRoundTripOffers = (
  selectedMapOffer,
  candidateMapOffers,
  filters = {}
) => {
  if (
    !selectedMapOffer ||
    !isRoundTripSuggestionEnabled(selectedMapOffer.exchangeType) ||
    !selectedMapOffer.from.location ||
    !selectedMapOffer.to.location ||
    !(selectedMapOffer.to.rawDate || selectedMapOffer.to.date) ||
    isPreplannedRoundTripOffer(selectedMapOffer)
  ) {
    return []
  }

  const candidateTypes =
    filters.exchangeTypes ||
    getRoundTripCandidateTypes(selectedMapOffer.exchangeType)
  const candidateTypeSet = new Set(candidateTypes)
  const maxDistanceKm = Number(filters.maxDistanceKm) || 0
  const daysAhead = Number(filters.daysAhead) || 0
  const shipownerIds = new Set((filters.shipowner || []).map(String))
  const containerIds = new Set((filters.container || []).map(String))
  const roundTripCenterMapLocation = getRoundTripCenterMapLocation(
    selectedMapOffer,
    candidateMapOffers
      .map(
        candidate => candidate && candidate.from && candidate.from.mapLocation
      )
      .filter(Boolean)
  )

  const matches = []
  candidateMapOffers.forEach(candidate => {
    if (
      !candidate ||
      !candidateTypeSet.has(candidate.exchangeType) ||
      (candidate.exchangeType !== EXCHANGE_MAP_TYPES.GENERAL &&
        !matchesRoundTripDictionaryFilter({
          candidateId: candidate.shipownerId,
          candidateName: candidate.shipownerName,
          filterIds: shipownerIds,
          sourceId: selectedMapOffer.shipownerId,
          sourceName: selectedMapOffer.shipownerName
        })) ||
      !matchesRoundTripDictionaryFilter({
        candidateId: candidate.containerId,
        candidateName: candidate.containerName,
        filterIds: containerIds,
        sourceId: selectedMapOffer.containerId,
        sourceName: selectedMapOffer.containerName
      }) ||
      (candidate.exchangeType === selectedMapOffer.exchangeType &&
        candidate.id === selectedMapOffer.id) ||
      !candidate.from.location ||
      !candidate.to.location ||
      !(candidate.from.rawDate || candidate.from.date) ||
      isPreplannedRoundTripOffer(candidate)
    ) {
      return
    }

    const distanceKm = getMapDistanceKm(
      roundTripCenterMapLocation,
      candidate.from.mapLocation
    )
    const locationMatches =
      distanceKm === null
        ? isSameOfferLocation(
            candidate.from.location,
            selectedMapOffer.to.location
          )
        : distanceKm <= maxDistanceKm
    const dateMatches = isDateWithinDays(
      selectedMapOffer.to.rawDate || selectedMapOffer.to.date,
      candidate.from.rawDate || candidate.from.date,
      daysAhead
    )
    const destinationMatches = isSameOfferLocation(
      candidate.to.location,
      selectedMapOffer.from.location
    )

    if (locationMatches && destinationMatches && dateMatches) {
      matches.push({
        ...candidate,
        roundTripDistanceKm: distanceKm || 0,
        roundTripListingTime: getRoundTripListingTime(candidate)
      })
    }
  })

  return matches.sort((firstCandidate, secondCandidate) => {
    const listingTimeDifference =
      secondCandidate.roundTripListingTime - firstCandidate.roundTripListingTime
    return (
      listingTimeDifference ||
      firstCandidate.roundTripDistanceKm - secondCandidate.roundTripDistanceKm
    )
  })
}

const getLocationGroupKey = (mapLocation, role) => {
  const coordinateKey = mapLocation.coordinates
    .map(coordinate => coordinate.toFixed(5))
    .join(':')
  return `${role}:${mapLocation.id || coordinateKey}`
}

export const groupOfferLocations = mapOffers => {
  const groups = new Map()
  const roles = ['from', 'to']

  mapOffers.forEach(mapOffer => {
    roles.forEach(role => {
      const endpoint = mapOffer[role]
      if (!endpoint.mapLocation) return

      const key = getLocationGroupKey(endpoint.mapLocation, role)
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          role,
          mapLocation: endpoint.mapLocation,
          offers: [],
          offersCount: 0,
          locationIds: [endpoint.mapLocation.id]
        })
      }
      const group = groups.get(key)
      group.offers.push(mapOffer)
      group.offersCount += 1
    })
  })

  return Array.from(groups.values())
}

// Keep endpoint roles separate here. Apart from making the map easier to read,
// this prevents a pickup location and a delivery location from ever becoming
// the same visual cluster.
export const clusterOfferLocations = (
  locationGroups,
  project,
  clusterRadius = 42
) => {
  if (
    typeof project !== 'function' ||
    !Number.isFinite(clusterRadius) ||
    clusterRadius <= 0
  ) {
    return locationGroups
  }

  const clusters = []
  const clusterBuckets = new Map()
  const getBucketKey = (clusterKey, x, y) => JSON.stringify([clusterKey, x, y])
  const addClusterToBucket = cluster => {
    const bucketKey = getBucketKey(
      cluster.clusterKey,
      cluster.bucketX,
      cluster.bucketY
    )
    if (!clusterBuckets.has(bucketKey)) clusterBuckets.set(bucketKey, new Set())
    clusterBuckets.get(bucketKey).add(cluster)
  }
  const removeClusterFromBucket = cluster => {
    const bucketKey = getBucketKey(
      cluster.clusterKey,
      cluster.bucketX,
      cluster.bucketY
    )
    const bucket = clusterBuckets.get(bucketKey)
    if (!bucket) return
    bucket.delete(cluster)
    if (bucket.size === 0) clusterBuckets.delete(bucketKey)
  }

  locationGroups.forEach(locationGroup => {
    const projectedPoint = project(locationGroup.mapLocation.coordinates)
    if (!projectedPoint) return
    const point = { x: Number(projectedPoint.x), y: Number(projectedPoint.y) }
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return
    const locationClusterKey = locationGroup.clusterKey || locationGroup.role
    const bucketX = Math.floor(point.x / clusterRadius)
    const bucketY = Math.floor(point.y / clusterRadius)

    let nearestCluster = null
    let nearestDistance = Infinity

    for (let x = bucketX - 1; x <= bucketX + 1; x += 1) {
      for (let y = bucketY - 1; y <= bucketY + 1; y += 1) {
        const nearbyClusters = clusterBuckets.get(
          getBucketKey(locationClusterKey, x, y)
        )
        if (nearbyClusters) {
          const nearbyClustersArray = Array.from(nearbyClusters)
          for (
            let clusterIndex = 0;
            clusterIndex < nearbyClustersArray.length;
            clusterIndex += 1
          ) {
            const cluster = nearbyClustersArray[clusterIndex]
            const dx = point.x - cluster.center.x
            const dy = point.y - cluster.center.y
            const distance = Math.sqrt(dx * dx + dy * dy)
            if (
              distance < clusterRadius &&
              (distance < nearestDistance ||
                (distance === nearestDistance &&
                  nearestCluster &&
                  cluster.order < nearestCluster.order))
            ) {
              nearestCluster = cluster
              nearestDistance = distance
            }
          }
        }
      }
    }

    if (!nearestCluster) {
      const cluster = {
        order: clusters.length,
        role: locationGroup.role,
        clusterKey: locationClusterKey,
        center: { x: point.x, y: point.y },
        bucketX,
        bucketY,
        locationGroups: [locationGroup]
      }
      clusters.push(cluster)
      addClusterToBucket(cluster)
      return
    }

    removeClusterFromBucket(nearestCluster)
    nearestCluster.locationGroups.push(locationGroup)
    const size = nearestCluster.locationGroups.length
    nearestCluster.center = {
      x: nearestCluster.center.x + (point.x - nearestCluster.center.x) / size,
      y: nearestCluster.center.y + (point.y - nearestCluster.center.y) / size
    }
    nearestCluster.bucketX = Math.floor(nearestCluster.center.x / clusterRadius)
    nearestCluster.bucketY = Math.floor(nearestCluster.center.y / clusterRadius)
    addClusterToBucket(nearestCluster)
  })

  return clusters.map(cluster => {
    const isCluster = cluster.locationGroups.length > 1
    const coordinates = cluster.locationGroups.reduce(
      (result, group) => [
        result[0] +
          group.mapLocation.coordinates[0] / cluster.locationGroups.length,
        result[1] +
          group.mapLocation.coordinates[1] / cluster.locationGroups.length
      ],
      [0, 0]
    )
    const offers = cluster.locationGroups.reduce(
      (result, group) => result.concat(group.offers),
      []
    )
    const offersCount = cluster.locationGroups.reduce(
      (result, group) => result + (Number(group.offersCount) || 0),
      0
    )
    const locationIds = Array.from(
      new Set(
        cluster.locationGroups.reduce(
          (result, group) => result.concat(group.locationIds || []),
          []
        )
      )
    )
    const points = cluster.locationGroups.reduce(
      (result, group) => result.concat(group.points || []),
      []
    )

    if (!isCluster) return cluster.locationGroups[0]

    const mostPopularLocationNames = cluster.locationGroups
      .slice()
      .sort((firstGroup, secondGroup) => {
        const offerCountDifference =
          secondGroup.offersCount - firstGroup.offersCount
        if (offerCountDifference !== 0) return offerCountDifference
        return firstGroup.mapLocation.name.localeCompare(
          secondGroup.mapLocation.name
        )
      })
      .slice(0, 2)
      .map(group => group.mapLocation.name)
    const clusterName = `${mostPopularLocationNames.join(', ')}${
      cluster.locationGroups.length > mostPopularLocationNames.length
        ? '...'
        : ''
    }`

    return {
      id: `cluster:${cluster.clusterKey}:${cluster.locationGroups
        .map(group => group.id)
        .sort()
        .join('|')}`,
      role: cluster.role,
      clusterKey: cluster.clusterKey,
      mapLocation: {
        coordinates,
        name: clusterName
      },
      offers,
      offersCount,
      locationIds,
      points,
      locationGroups: cluster.locationGroups,
      isCluster: true
    }
  })
}

export const getDirectRoute = mapOffer => {
  if (!mapOffer || !mapOffer.from.mapLocation || !mapOffer.to.mapLocation) {
    return []
  }
  return [
    mapOffer.from.mapLocation.coordinates,
    mapOffer.to.mapLocation.coordinates
  ]
}

export const getRoundTripRoutesKey = matches =>
  JSON.stringify(
    matches
      .map(match => ({
        id: getRoundTripOfferKey(match),
        exchangeType: match.exchangeType,
        endpoints: getDirectRoute(match)
      }))
      .filter(route => route.endpoints.length === 2)
      .sort((first, second) => first.id.localeCompare(second.id))
  )

export const getRoutesGeoJson = routes => ({
  type: 'FeatureCollection',
  features: routes
    .filter(route => route.coordinates && route.coordinates.length > 1)
    .map(route => ({
      type: 'Feature',
      properties: {
        featureKind: 'route',
        connectionId: route.id || '',
        exchangeType: route.exchangeType || '',
        routeIndex: Number.isInteger(route.routeIndex) ? route.routeIndex : -1,
        roundTrip: Boolean(route.roundTrip),
        selected: Boolean(route.selected)
      },
      geometry: { type: 'LineString', coordinates: route.coordinates }
    }))
})

export const getRouteGeoJson = (coordinates, exchangeType) =>
  getRoutesGeoJson([{ coordinates, exchangeType }])
